import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'lib/../i18n';
import { Trans, useTranslation } from 'react-i18next';
import type { Dictionary } from 'lodash';
import pick from 'lodash/pick';
import { createSelector } from '@reduxjs/toolkit';
import { ModalProps, useDisclosure } from '@chakra-ui/react';
import {
  useForm,
  FormProvider,
  useFormContext,
  SubmitHandler,
} from 'react-hook-form';
import { faDisplay, faPhoneRotary } from '@fortawesome/pro-solid-svg-icons';

import untranslatedSteps from '@backend/lib/lead/steps';
import { useFormDefaults } from 'lib/form';
import { useAppSelector } from 'store/configureStore';
import { selectLead, selectLeadInventory } from 'slices/leadSlice';
import useStepMethods, { UseStepMethodsReturn } from 'hooks/useStepMethods';
import StepSummary from 'components/StepSummary';
import RhfStyledRadioField from 'components/fields/RhfStyledRadioField';
import UlfFormStep from 'components/UlfFormStep';
import { SkipInventoryDialog } from 'components/SkipInventoryButton';

const STEP_NAME = 'nextSteps';

const useSelectNextStepsStepValues = () =>
  useAppSelector(
    createSelector([selectLead], (lead) => pick(lead, ['roomsStart'])),
  );

export const NextStepsStepSummary = () => {
  const { t } = useTranslation();
  const stepDefinition = untranslatedSteps(t)[STEP_NAME];
  const values = useSelectNextStepsStepValues();
  const isValid = stepDefinition.isValid(values);
  return (
    <StepSummary stepKey={STEP_NAME} isValid={isValid}>
      {isValid && values.roomsStart === true && <Trans>Online now</Trans>}
      {isValid && values.roomsStart === false && <Trans>Over the phone</Trans>}
    </StepSummary>
  );
};

const schema = z.object({
  roomsStart: z.string(),
});

export type NextStepsStepFormValues = z.infer<typeof schema>;

export const NextStepsStepForm = ({
  hasInventory,
  onContinue,
  onSave,
  isDialogOpen,
  onDialogClose,
  onConfirmFinish,
  onCancelFinish,
}: UseStepMethodsReturn & {
  hasInventory: boolean;
  isDialogOpen: ModalProps['isOpen'];
  onDialogClose: ModalProps['onClose'];
  onConfirmFinish(): void;
  onCancelFinish(): void;
}) => {
  const { t } = useTranslation();
  const stepDefinition = untranslatedSteps(t)[STEP_NAME];
  const { handleSubmit, formState, control } =
    useFormContext<NextStepsStepFormValues>();
  const { isSubmitting, isValid } = formState;

  return (
    <UlfFormStep
      title={stepDefinition.title}
      description={stepDefinition.description}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isValid={isValid}
      onContinue={onContinue}
      onSave={onSave}
    >
      <RhfStyledRadioField
        label={t('How would you like to tell us about your belongings?')}
        isRequired={true}
        control={control}
        name="roomsStart"
        options={[
          {
            value: '0',
            label: <Trans>Over the phone</Trans>,
            icon: faPhoneRotary,
            props: {
              w: { base: '100%', sm: 'inherit' },
            },
          },
          {
            value: '1',
            label: <Trans>Online now</Trans>,
            icon: faDisplay,
            props: {
              w: { base: '100%', sm: 'inherit' },
            },
          },
        ]}
      />
      <SkipInventoryDialog
        isOpen={isDialogOpen}
        onClose={onDialogClose}
        onConfirmFinish={onConfirmFinish}
        onCancelFinish={onCancelFinish}
        hasInventory={hasInventory}
      />
    </UlfFormStep>
  );
};

const NextStepsStep = () => {
  const { onSave, onContinue } = useStepMethods(STEP_NAME);
  const { roomsStart } = useSelectNextStepsStepValues();
  const inventory = useAppSelector(selectLeadInventory);
  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure();

  const form = useForm<NextStepsStepFormValues>({
    ...useFormDefaults,
    resolver: zodResolver(schema),
    defaultValues: {
      roomsStart:
        typeof roomsStart !== 'undefined' ? `${Number(roomsStart)}` : undefined,
    },
  });

  const onConfirmFinish = async () => {
    onDialogClose();

    return onSave
      ? onSave({
          roomsStart: false,
        })
      : onContinue({ roomsStart: false });
  };

  const onCancelFinish = async () => {
    onDialogClose();

    if (inventory.length > 0) {
      return onSave
        ? onSave({
            roomsStart: true,
          })
        : onContinue({ roomsStart: true });
    }
  };

  const onSubmit = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fn?: (arg: Dictionary<any>) => void,
  ): SubmitHandler<NextStepsStepFormValues> => {
    return async ({ roomsStart: roomsStartValue }: NextStepsStepFormValues) => {
      const roomsStart = roomsStartValue
        ? Boolean(parseInt(roomsStartValue, 10))
        : undefined;

      if (!roomsStart) {
        return onDialogOpen();
      }

      return (
        fn &&
        fn({
          roomsStart,
        })
      );
    };
  };

  return (
    <FormProvider {...form}>
      <NextStepsStepForm
        onSave={onSave ? onSubmit(onSave) : undefined}
        onContinue={onSubmit(onContinue)}
        isDialogOpen={isDialogOpen}
        onDialogClose={onDialogClose}
        onConfirmFinish={onConfirmFinish}
        onCancelFinish={onCancelFinish}
        hasInventory={inventory.length > 0}
      />
    </FormProvider>
  );
};

const NextStepsStepContainer = ({
  isCurrentStep,
}: {
  isCurrentStep: boolean;
}) => {
  return isCurrentStep ? <NextStepsStep /> : <NextStepsStepSummary />;
};

export default NextStepsStepContainer;
